import { type PropsWithChildren } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";

interface Props {
  isOpen: boolean;
  onOpen(): void;
  onClose(): void;
}

export const FilterPopover = ({
  isOpen,
  children,
  onOpen,
  onClose,
}: PropsWithChildren<Props>) => {
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (value) => (value ? onOpen() : onClose()),
    transform: false,
    placement: "bottom",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <div>
      <button
        className={clsx(
          "flex justify-center items-center cursor-pointer",
          "rounded w-6 h-6 text-[0.65rem] ml-0.5",
          {
            "text-cosmic bg-cosmic-3": !isOpen,
            "text-white bg-primary": isOpen,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <i className={clsx("fa-regular fa-sharp fa-filter")}></i>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            className={clsx(
              "absolute right-0 mt-2 px-4 pb-4 pt-4 w-52 z-[1000]",
              "bg-white rounded-lg elevation-1 text-sm",
            )}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
